import { Table, Tag } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DAY_FORMAT, DAYTIME_FORMAT } from '../../constants';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import useColumnConfig from '../../hooks/useColumnConfig';
import ColumnConfigDrawer from '../ColumnConfigDrawer';

const MOBILE_HIDDEN_KEYS = ['travelMins', 'workingMins', 'serviceMins', 'workingPercent', 'orders', 'collected'];
const FIXED_KEYS = ['id', 'action'];

export default function RoutesTable({
  withAction,
  dataSource,
  selectedRowKeys,
  onSelectChange,
  partnerId,
  configRef,
  onPageSizeChange,
  ...props
}) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { t } = useTranslation('routes');
  const { t: tc } = useTranslation('common');

  const allColumns = useMemo(() => {
    const cols = [
      {
        title: t('id'),
        dataIndex: 'id',
        key: 'id',
        fixed: 'left',
        width: 100,
        sorter: true,
        defaultSortOrder: 'descend',
      },
      {
        title: tc('date'),
        dataIndex: 'date',
        key: 'date',
        width: 200,
        className: 'border-right',
        sorter: true,
        render: (value) => (value ? moment(value).format(DAY_FORMAT) : ''),
      },
      {
        title: t('driver'),
        dataIndex: 'driverName',
        key: 'driverName',
        render: (_va, record) => record?.driver?.name || '-',
      },
      {
        title: t('stops'),
        key: 'stops',
        render: (record) => record?._count?.steps || 0,
      },
      {
        title: t('distance'),
        dataIndex: 'distance',
        key: 'distance',
        render: (value) => (value ? `${(value / 1000).toFixed(2)} km` : ''),
      },
      {
        title: t('travelTime'),
        dataIndex: 'travelMins',
        key: 'travelMins',
        render: (value) => (value ? `${(value / 60).toFixed(2)} hr` : ''),
      },
      {
        title: t('workingTime'),
        dataIndex: 'workingMins',
        key: 'workingMins',
        render: (value) => (value ? `${(value / 60).toFixed(2)} hr` : ''),
      },
      {
        title: t('serviceTime'),
        dataIndex: 'serviceMins',
        key: 'serviceMins',
        render: (value) => (value ? `${(value / 60).toFixed(2)} hr` : ''),
      },
      {
        title: t('workingPercent'),
        dataIndex: 'workingPercent',
        key: 'workingPercent',
        render: (value) => (value ? `${value}%` : ''),
      },
      {
        title: t('orders'),
        key: 'orders',
        render: (record) => {
          const totalOrders = record?._count?.orders || 0;
          const partnerOrders = record?.partnerOrderCount;
          const hasPartnerContext = partnerOrders !== undefined && partnerOrders !== null;
          const displayCount = hasPartnerContext ? partnerOrders : totalOrders;

          if (displayCount === 0 && !hasPartnerContext) return '0';

          const orderUrl = partnerId
            ? `/admin/orders?current=1&routeId=${record.id}&partnerId=${partnerId}`
            : `/admin/orders?current=1&routeId=${record.id}`;
          const link = (
            <Link to={orderUrl} onClick={(e) => e.stopPropagation()}>
              {displayCount}
            </Link>
          );

          if (hasPartnerContext && totalOrders > partnerOrders) {
            return (
              <span>
                {link}
                {/*<span style={{ color: '#999', fontSize: 12 }}> / {totalOrders}</span>*/}
              </span>
            );
          }

          return link;
        },
      },
      {
        title: t('collected'),
        key: 'collected',
        width: 140,
        render: (record) => {
          const items = record.collectedItemsByPartner;
          if (!items || items.length === 0) return '-';
          return items.map((p) => (
            <Tag key={p.code} icon={<InboxOutlined />} color="orange" style={{ marginBottom: 2 }}>
              {p.code}: {p.count}
            </Tag>
          ));
        },
      },
      {
        title: tc('status'),
        dataIndex: 'status',
        key: 'status',
        width: 150,
        render: (value, record) => {
          let label;
          if (value === 'in_progress') {
            label = tc('orderStatuses.IN_PROGRESS');
          } else if (value === 'completed') {
            label = tc('orderStatuses.COMPLETED');
          } else if (value === 'dispatched') {
            const actionDisplay = record.actionStatus
              ? tc(`actionRouteStatuses.${record.actionStatus.toUpperCase()}`) || record.actionStatus
              : '';
            label = actionDisplay || tc('orderStatuses.DISPATCHED');
          } else {
            label = value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';
          }
          return <span className={'status order ' + (value || '').toLowerCase()}>{label}</span>;
        },
      },
      {
        title: tc('updatedAt'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 250,
        render: (value) => (value ? moment(value).format(DAYTIME_FORMAT) : ''),
      },
    ];

    if (withAction) {
      cols.push({
        title: '',
        key: 'action',
        width: 120,
        fixed: 'right',
        render(record) {
          return withAction(record);
        },
        className: 'border-left',
      });
    }

    return cols;
  }, [withAction, partnerId]);

  const columnConfig = useColumnConfig('routes-columns', allColumns, FIXED_KEYS);

  // Expose openDrawer to parent via ref
  if (configRef) {
    configRef.current = { openDrawer: columnConfig.openDrawer, pageSize: columnConfig.pageSize };
  }

  const displayColumns = isMobile
    ? columnConfig.visibleColumns.filter((col) => !MOBILE_HIDDEN_KEYS.includes(col.key))
    : columnConfig.visibleColumns;

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div className="RoutesTable table-common">
      <Table
        columns={displayColumns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        {...props}
      />
      <ColumnConfigDrawer
        open={columnConfig.drawerOpen}
        onClose={columnConfig.closeDrawer}
        columns={columnConfig.orderedAllColumns}
        hidden={columnConfig.hidden}
        fixedKeys={FIXED_KEYS}
        onToggle={columnConfig.toggleColumn}
        onMove={columnConfig.moveColumn}
        onReset={columnConfig.resetConfig}
        pageSize={columnConfig.pageSize}
        onPageSizeChange={(size) => {
          columnConfig.setPageSize(size);
          onPageSizeChange?.(size);
        }}
      />
    </div>
  );
}

// Export the config opener for use in page headers
export { default as useColumnConfig } from '../../hooks/useColumnConfig';
